import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainHeader from '../components/MainHeader.js';
// 1. Import Firestore db and functions
import { db } from '../firebase.js'; 
import { collection, addDoc, serverTimestamp } from "firebase/firestore"; 

function NewApplicationPage() {
  const navigate = useNavigate();

  // 2. Set up state for all form fields
  const [formData, setFormData] = useState({
    mdroNo: '',
    srNo: '',
    mdpNo: '',
    leaseNo: '',
    barcode: '',
    leaseName: '',
    landType: '',
    dscName: '',
    gstNo: '',
    district: '',
    loadingPlace: '',
    purpose: '',
    mineral: '',
    quantity: '',
    amount: '',
    buyer: '',
    route: '',
    checkPost: '',
    destination: '',
    vehicleInfo: '',
    // === CHANGED ===
    validityStart: '',
    validityEnd: '',
    // =================
    distance: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 3. Handle changes to any input field
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // 4. Handle the form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return; // Prevent double submit
    setIsSubmitting(true);
    
    try {
      // Add a new document to the "applications" collection
      const docRef = await addDoc(collection(db, "applications"), {
        ...formData,
        createdAt: serverTimestamp() // Add a server timestamp
      });
      
      console.log("Document written with ID: ", docRef.id);
      setIsSubmitting(false);
      
      // Navigate to the dashboard on success
      navigate('/dashboard'); 

    } catch (e) {
      console.error("Error adding document: ", e);
      setIsSubmitting(false);
      alert("Error saving application. Please try again.");
    }
  };

  const handleCancel = () => {
    navigate(-1); // Go back
  };

  return (
    <div className="relative flex h-auto min-h-screen w-full flex-col bg-page-bg/90 dark:bg-background-dark/90 group/design-root overflow-x-hidden">
      <div className="layout-container flex h-full grow flex-col">
        <MainHeader />
        <main className="flex flex-1 justify-center py-8 px-4 sm:px-6 lg:px-8">
          <div className="w-full max-w-4xl">
            {/* ... (form header, no changes) ... */}
            <div className="flex flex-wrap justify-between gap-3 p-4 mb-6">
              <div className="flex min-w-72 flex-col gap-3">
                <p className="text-text-color dark:text-text-dark text-4xl font-black leading-tight tracking-[-0.033em]">
                  New Mineral Dispatch Permit
                </p>
                <p className="text-text-color/70 dark:text-subtext-dark text-base font-normal leading-normal">
                  Please fill in all the required information below to generate a new Mineral Dispatch and Royalty Order.
                </p>
              </div>
            </div>
            
            <div className="bg-input-bg/90 dark:bg-surface-dark/90 rounded-xl shadow-sm p-6 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-8">
                
                {/* Section 1: Lease & Identification Details (no changes) */}
                <section>
                  <h2 className="text-section-header dark:text-text-dark text-[22px] font-bold leading-tight tracking-[-0.015em] pb-5 border-b border-border-color dark:border-border-dark mb-6">
                    Lease & Identification Details
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                    <label className="flex flex-col">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">MDRO No *</p>
                      <input name="mdroNo" value={formData.mdroNo} onChange={handleChange} className="form-input" placeholder="Enter MDRO Number" required />
                    </label>
                    <label className="flex flex-col">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">Sr No *</p>
                      <input name="srNo" value={formData.srNo} onChange={handleChange} className="form-input" placeholder="Enter Serial Number" required />
                    </label>
                    <label className="flex flex-col">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">MDP No *</p>
                      <input name="mdpNo" value={formData.mdpNo} onChange={handleChange} className="form-input" placeholder="Enter MDP Number" required />
                    </label>
                    <label className="flex flex-col">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">Lease No *</p>
                      <input name="leaseNo" value={formData.leaseNo} onChange={handleChange} className="form-input" placeholder="Enter Lease Number" required />
                    </label>
                    <label className="flex flex-col">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">Barcode</p>
                      <input name="barcode" value={formData.barcode} onChange={handleChange} className="form-input" placeholder="Scan or enter barcode" />
                    </label>
                    <label className="flex flex-col">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">Lease Name *</p>
                      <input name="leaseName" value={formData.leaseName} onChange={handleChange} className="form-input" placeholder="Enter Lease Name" required />
                    </label>
                    <label className="flex flex-col">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">Type of Land *</p>
                      <input name="landType" value={formData.landType} onChange={handleChange} className="form-input" placeholder="e.g., Private, Government" required />
                    </label>
                    <label className="flex flex-col">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">DSC No / Name *</p>
                      <input name="dscName" value={formData.dscName} onChange={handleChange} className="form-input" placeholder="Enter DSC number or name" required />
                    </label>
                    <label className="flex flex-col md:col-span-2">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">GST No</p>
                      <input name="gstNo" value={formData.gstNo} onChange={handleChange} className="form-input" placeholder="Enter 15-digit GST Number" />
                    </label>
                  </div>
                </section>
                
                {/* Section 2: Dispatch Information (no changes) */}
                <section>
                  <h2 className="text-section-header dark:text-text-dark text-[22px] font-bold leading-tight tracking-[-0.015em] pb-5 border-b border-border-color dark:border-border-dark mb-6">
                    Dispatch Information
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                    <label className="flex flex-col">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">Taluk / District *</p>
                      <input name="district" value={formData.district} onChange={handleChange} className="form-input" placeholder="e.g., Alur / Hassan" required />
                    </label>
                    <label className="flex flex-col">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">Place of Loading *</p>
                      <input name="loadingPlace" value={formData.loadingPlace} onChange={handleChange} className="form-input" placeholder="Enter loading location" required />
                    </label>
                    <label className="flex flex-col md:col-span-2">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">MDRO Purpose *</p>
                      <input name="purpose" value={formData.purpose} onChange={handleChange} className="form-input" placeholder="Enter purpose of dispatch" required />
                    </label>
                    <label className="flex flex-col">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">Mineral / Grade *</p>
                      <input name="mineral" value={formData.mineral} onChange={handleChange} className="form-input" placeholder="e.g., Iron Ore / Grade A" required />
                    </label>
                    <label className="flex flex-col">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">Quantity (Tonnes) *</p>
                      <input name="quantity" value={formData.quantity} onChange={handleChange} className="form-input" placeholder="e.g., 50.5" type="number" required />
                    </label>
                    <label className="flex flex-col">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">Total Amount Paid *</p>
                      <input name="amount" value={formData.amount} onChange={handleChange} className="form-input" placeholder="e.g., 125000" type="number" required />
                    </label>
                    <label className="flex flex-col">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">Buyer *</p>
                      <input name="buyer" value={formData.buyer} onChange={handleChange} className="form-input" placeholder="Enter buyer's name or ID" required />
                    </label>
                  </div>
                </section>

                {/* Section 3: Vehicle & Route Details */}
                <section>
                  <h2 className="text-section-header dark:text-text-dark text-[22px] font-bold leading-tight tracking-[-0.015em] pb-5 border-b border-border-color dark:border-border-dark mb-6">
                    Vehicle & Route Details
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                    <label className="flex flex-col md:col-span-2">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">Route *</p>
                      <input name="route" value={formData.route} onChange={handleChange} className="form-input" placeholder="Describe the planned route" required />
                    </label>
                    <label className="flex flex-col">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">Check Post *</p>
                      <input name="checkPost" value={formData.checkPost} onChange={handleChange} className="form-input" placeholder="Enter check post name" required />
                    </label>
                    <label className="flex flex-col">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">Destination *</p>
                      <input name="destination" value={formData.destination} onChange={handleChange} className="form-input" placeholder="Enter final destination address" required />
                    </label>
                    <label className="flex flex-col md:col-span-2">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">Vehicle No/No of wheels/type/mobile No *</p>
                      <input name="vehicleInfo" value={formData.vehicleInfo} onChange={handleChange} className="form-input" placeholder="e.g., KA-01-AB-1234 / 10 / Truck / 9876543210" required />
                    </label>
                    
                    {/* === CHANGED === */}
                    <label className="flex flex-col">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">Validity Start (Date & Time) *</p>
                      <input name="validityStart" value={formData.validityStart} onChange={handleChange} className="form-input" type="datetime-local" required />
                    </label>
                    <label className="flex flex-col">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">Validity End (Date & Time) *</p>
                      <input name="validityEnd" value={formData.validityEnd} onChange={handleChange} className="form-input" type="datetime-local" required />
                    </label>
                    {/* ================= */}

                    <label className="flex flex-col">
                      <p className="text-text-color dark:text-text-dark text-sm font-medium leading-normal pb-2">Distance *</p>
                      <input name="distance" value={formData.distance} onChange={handleChange} className="form-input" placeholder="Enter total distance" type="number" required />
                    </label>
                  </div>
                </section>
                
                {/* Form Actions (no changes) */}
                <div className="flex flex-col sm:flex-row items-center justify-end gap-4 pt-6 mt-8 border-t border-border-color dark:border-border-dark">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="w-full sm:w-auto flex cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 bg-secondary-button text-text-color dark:bg-background-dark dark:text-text-dark gap-2 text-sm font-bold leading-normal tracking-[0.015em] px-6 border border-border-color dark:border-border-dark hover:bg-[#A8B8A0] dark:hover:bg-white/5 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting} 
                    className="w-full sm:w-auto flex cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 bg-primary text-white gap-2 text-sm font-bold leading-normal tracking-[0.015em] px-6 hover:bg-hover-color transition-colors duration-200 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Saving...' : 'Generate MDRO'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default NewApplicationPage;